// Manages per-item draggable, overlay, and transform logic for ZoomPanDraggable
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  addStageOffset,
  measureStageRect,
  rectsAreEqual,
  stageRectToViewRect,
  subtractStageOffset,
  viewRectToStageRect,
  getBounds as getBoundsHelper,
} from './zoomPanDraggableUtils'

const EMPTY_STAGE_RECT = { x: 0, y: 0, width: 0, height: 0 }

function normalizeOffset(offset) {
  if (!offset || typeof offset !== 'object') {
    return { x: 0, y: 0 }
  }
  const { x, y } = offset
  return {
    x: Number.isFinite(x) ? x : 0,
    y: Number.isFinite(y) ? y : 0,
  }
}

export default function useDraggableItem({
  initialPosition,
  minSize,
  stickerBoundsRef,
  controllerRef,
  stageRef,
  zoomPanUtils,
  onPositionCommit,
  onActivate,
  pageOffset,
  isZoomPanReady,
}) {
  const zoomTimeoutRef = useRef()
  const contentRef = useRef(null)
  const pageStageRectRef = useRef(null)
  const normalizedInitialOffset = normalizeOffset(pageOffset)
  const pageOffsetRef = useRef(normalizedInitialOffset)
  const [pageStageRect, setPageStageRect] = useState(null)
  const [contentStagePosition, setContentStagePosition] = useState(() => ({ ...initialPosition }))
  const contentStagePositionRef = useRef({ ...initialPosition })
  const [overlayerPosition, setOverlayerPosition] = useState({
    height: initialPosition.height,
    width: initialPosition.width,
    x: initialPosition.x + normalizedInitialOffset.x,
    y: initialPosition.y + normalizedInitialOffset.y,
  })
  const [isOverlayActive, setIsOverlayActive] = useState(false)

  const handleActivate = useCallback(() => {
    setIsOverlayActive((prev) => {
      if (!prev) {
        if (typeof onActivate === 'function') {
          onActivate()
        }
        return true
      }
      return prev
    })
  }, [onActivate])

  useEffect(() => {
    contentStagePositionRef.current = contentStagePosition
  }, [contentStagePosition])

  useEffect(() => {
    if (pageStageRect) {
      pageStageRectRef.current = pageStageRect
    }
  }, [pageStageRect])

  const withPageOffset = useCallback((rect) => {
    const baseRect = rect ?? EMPTY_STAGE_RECT
    const offset = pageOffsetRef.current
    return {
      ...baseRect,
      x: (baseRect.x ?? 0) + offset.x,
      y: (baseRect.y ?? 0) + offset.y,
    }
  }, [])

  const measurePageStageRect = useCallback(() => {
    const controller = controllerRef.current
    const element = stickerBoundsRef?.current ?? stageRef?.current ?? null
    if (!controller || !element) return null
    const measured = measureStageRect({
      controller,
      element,
      getBoundingRect: zoomPanUtils?.getBoundingRect,
    })
    return measured
  }, [controllerRef, stickerBoundsRef, stageRef, zoomPanUtils])

  const recomputeLayout = useCallback(() => {
    const controller = controllerRef.current
    if (!controller) return
    const measuredRect = measurePageStageRect()
    if (measuredRect) {
      const previous = pageStageRectRef.current
      pageStageRectRef.current = measuredRect
      if (!previous || !rectsAreEqual(previous, measuredRect)) {
        setPageStageRect(measuredRect)
      }
    }
    const pageRect = pageStageRectRef.current ?? measuredRect ?? EMPTY_STAGE_RECT
    const offsetPageRect = withPageOffset(pageRect)
    const absoluteStageRect = addStageOffset(contentStagePositionRef.current, offsetPageRect)
    const nextViewRect = stageRectToViewRect(controller, absoluteStageRect)
    setOverlayerPosition((prev) => (rectsAreEqual(prev, nextViewRect) ? prev : nextViewRect))
  }, [controllerRef, measurePageStageRect, withPageOffset])

  const scheduleLayoutRecalculation = useCallback(() => {
    if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current)
    zoomTimeoutRef.current = setTimeout(() => {
      recomputeLayout()
    }, 100)
  }, [recomputeLayout])

  useLayoutEffect(() => {
    if (!isZoomPanReady) {
      return () => {}
    }
    recomputeLayout()
    if (zoomTimeoutRef.current) clearTimeout(zoomTimeoutRef.current)
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current)
      }
    }
  }, [recomputeLayout, isZoomPanReady])

  useEffect(() => {
    if (!isZoomPanReady) {
      return
    }
    pageOffsetRef.current = normalizeOffset(pageOffset)
    scheduleLayoutRecalculation()
  }, [pageOffset, scheduleLayoutRecalculation, isZoomPanReady])

  useEffect(() => {
    if (!isZoomPanReady) {
      return undefined
    }
    if (!initialPosition) return undefined
    const nextPosition = { ...initialPosition }
    if (rectsAreEqual(contentStagePositionRef.current, nextPosition)) {
      return undefined
    }
    contentStagePositionRef.current = nextPosition
    setContentStagePosition(nextPosition)
    scheduleLayoutRecalculation()
    // Note: No cleanup needed here - scheduleLayoutRecalculation() clears any previous timeout,
    // and unmount cleanup is handled by the useLayoutEffect above
    return undefined
  }, [initialPosition, scheduleLayoutRecalculation, isZoomPanReady])

  useEffect(() => {
    const observedElement = stickerBoundsRef?.current ?? stageRef?.current
    if (!observedElement) return undefined
    if (typeof ResizeObserver === 'undefined') {
      scheduleLayoutRecalculation()
      return undefined
    }
    const observer = new ResizeObserver(() => {
      if (!isZoomPanReady) {
        return
      }
      scheduleLayoutRecalculation()
    })
    observer.observe(observedElement)
    return () => {
      observer.disconnect()
    }
  }, [stickerBoundsRef, scheduleLayoutRecalculation, stageRef, isZoomPanReady])

  useEffect(() => {
    const controller = controllerRef.current
    if (!controller) return undefined
    const changeListener = () => {
      if (!isZoomPanReady) {
        return
      }
      scheduleLayoutRecalculation()
    }
    controller.addOnChange(changeListener)
    return () => {
      controller.removeOnChange(changeListener)
    }
  }, [controllerRef, isZoomPanReady, scheduleLayoutRecalculation])

  const updatePositionsFromView = useCallback(
    (viewRect, { syncOverlay = false, detectZoomBounceback = false } = {}) => {
      const controller = controllerRef.current
      if (!controller) return
      let pageRect = pageStageRectRef.current
      if (!pageRect) {
        const measuredRect = measurePageStageRect()
        if (measuredRect) {
          pageStageRectRef.current = measuredRect
          setPageStageRect(measuredRect)
          pageRect = measuredRect
        }
      }
      pageRect = pageRect ?? EMPTY_STAGE_RECT
      const offsetPageRect = withPageOffset(pageRect)
      const stageAbsolute = viewRectToStageRect(controller, viewRect)
      const stageRelative = subtractStageOffset(stageAbsolute, offsetPageRect)

      // ZOOM BOUNCEBACK DETECTION:
      // During zoom bounceback, size changes dramatically (20-50px per frame)
      // Skip position updates to prevent sticker resize
      if (detectZoomBounceback && !controller.isDrawing) {
        const sizeDelta =
          Math.abs(stageRelative.width - contentStagePositionRef.current.width) +
          Math.abs(stageRelative.height - contentStagePositionRef.current.height)

        // Threshold chosen based on observed behavior:
        // - Normal zoom operations: < 10px size change
        // - Sticker operations: 0px size change (constant size)
        // - Zoom bounceback: 15-25px per frame
        // Setting at 10px provides safe margin
        if (sizeDelta > 10) {
          return // Skip update during zoom bounceback
        }
      }

      if (!rectsAreEqual(contentStagePositionRef.current, stageRelative)) {
        contentStagePositionRef.current = stageRelative
        setContentStagePosition(stageRelative)
      }

      if (syncOverlay) {
        setOverlayerPosition((prev) => (rectsAreEqual(prev, viewRect) ? prev : viewRect))
      }
    },
    [controllerRef, measurePageStageRect, withPageOffset]
  )

  const handlePositionChange = useCallback(
    (newViewRect) => {
      updatePositionsFromView(newViewRect, { syncOverlay: true })
    },
    [updatePositionsFromView]
  )

  const onAnimationChange = useCallback(
    (newViewRect) => {
      updatePositionsFromView(newViewRect, { detectZoomBounceback: true })
    },
    [updatePositionsFromView]
  )

  const handleOverlayFocus = useCallback(() => {
    setIsOverlayActive(true)
  }, [])

  const handleOverlayBlur = useCallback((event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsOverlayActive(false)
    }
  }, [])

  const getBounds = useCallback(() => {
    return getBoundsHelper({
      controller: controllerRef.current,
      pageRect: pageStageRectRef.current,
      stageRef,
      minSize,
      stageRectToViewRectFn: stageRectToViewRect,
    })
  }, [controllerRef, minSize, stageRef])

  function onDragEnd() {
    controllerRef.current.isDrawing = false
    if (typeof onPositionCommit === 'function') {
      onPositionCommit(contentStagePositionRef.current)
    }
  }

  function onDragStart() {
    controllerRef.current.isDrawing = true
  }

  return {
    contentRef,
    contentStagePosition,
    overlayerPosition,
    isOverlayActive,
    handleActivate,
    handlePositionChange,
    onAnimationChange,
    handleOverlayFocus,
    handleOverlayBlur,
    getBounds,
    onDragEnd,
    onDragStart,
  }
}
